import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Grid, Box, Button, Typography } from '@mui/material';
import { isEqual } from 'lodash';

import PageContainer from '@shared/components/container/PageContainer';
import Spinner from '@shared/components/spinner/Spinner';
import { AnimatedContent } from '@shared/components/AnimatedOutlet';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';

import { toast } from 'react-toastify';
import { CustomErrorToast } from '@shared/components/customErrorToast';

import EditCampaignSidebar from './EditCampaignSidebar/EditCampaignSidebar';
import { SidebarTabNames } from './constants';
import { SidebarTabNamesType } from './types';
import {
  CampaignDto,
  useCampaignUpdateControllerCampaignUpdateMutation,
  useCampaignSipTrunkControllerGetSipTrunkQuery,
  useCampaignControllerCampaignQuery,
  useCompanyControllerGetCompanyQuery,
  useCustomerListPaginatedControllerGetCustomerListPaginatedQuery,
} from '@shared/services/apiService/apiService';
import { AuthenticatedPath } from '@shared/constants/routes.constants';
import { RootState, useAppSelector } from '@shared/store';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEditCampaign } from '@features/Admin/Campaign/hooks/useEditCampaign';
import { useCampaignRealtimeSync } from '@features/Admin/Campaign/hooks/useCampaignRealtimeSync';
import { useEditCampaignLayout } from '@features/Admin/Campaign/hooks/useEditCampaignLayout';
import { useEditCampaignTabRouting } from '@features/Admin/Campaign/hooks/useEditCampaignTabRouting';
import { useCampaignStartValidation } from '@features/Admin/Campaign/hooks/useCampaignStartValidation';
import { useCampaignStartStop } from '@features/Admin/Campaign/hooks/useCampaignStartStop';
import {
  CampaignGeneralFormValues,
  getCampaignGeneralFormValues,
  mapCampaignDtoToFormValues,
} from '@features/Admin/Campaign/utils/edit-campaign-mapper';
import { CampaignRunStatusType } from '@shared/services/apiService/missing-types';
import { useTranslation } from 'react-i18next';
import { FormProvider, useFormState } from 'react-hook-form';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import { useCampaignAgents } from '@features/Admin/Agent/hooks/useCampaignAgents';
import type { CreateCampaignFormValue } from '@features/Admin/Campaign/model/type';
import { useCampaignAmbienceSettings } from '@features/Admin/AmbienceSettings';
import { useCampaignNoiseProcessingSettings } from '@features/Admin/NoiseProcessing';
import {
  ConversationSettingsDraft,
  ConversationSettingsValidation,
  mapConversationSettingsDraftToRequest,
  useConversationSettingsController,
} from '@features/Admin/ConversationSettings';
import { useBeforeUnload, useBlocker } from '@shared/hooks';
import { formatCompanyDisplayName } from '@shared/utils/companyDisplayName';
import {
  getRemovedAgentSettingNameKeys,
  getRemovedAgentSettings,
} from '@shared/utils/removedAgentSettings';
import type {
  AgentRemovedSettingsBlockerInput,
  AgentSettingsCheckStatus,
} from '@features/Admin/Campaign/utils/campaign-validation.utils';
import { EditCampaignTabContent } from './EditCampaignTabContent';
import { EditCampaignDialogs } from './EditCampaignDialogs';
import type { SipTrunkActions } from './SidebarTabs/SipTrunkTab';
import type { CallAnalysisActions } from './SidebarTabs/analysis/CallAnalysisStructuredOutputSection';
import {
  CampaignSectionSaveActions,
  EditCampaignSectionActions,
} from './EditCampaignSectionActions';

const UUID_ROUTE_PARAM_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const GENERAL_CAMPAIGN_FIELDS = [
  'title',
  'reservedLimit',
  'retryIntervalInMinutes',
  'retryMaxNumber',
  'retriesPerDay',
  'callSchedule',
] as const;

const extractFirstErrorMessage = (value: unknown): string | undefined => {
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = typeof item === 'string' ? item : extractFirstErrorMessage(item);
      if (found) return found;
    }
    return undefined;
  }
  if (value && typeof value === 'object') {
    return extractFirstErrorMessage(Object.values(value));
  }
  return undefined;
};

export const EditCampaignPage: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useAppSelector((state: RootState) => state.auth.authenticatedUser);
  const currentCompany = useAppSelector((state: RootState) => state.auth.authenticatedUserCompany);
  const [updateConversationSettings, { isLoading: isSavingConversationSettings }] =
    useCampaignUpdateControllerCampaignUpdateMutation();
  const [updateCampaignProductDetails] = useCampaignUpdateControllerCampaignUpdateMutation();

  const [leadListChanged, setLeadListChanged] = useState(false);
  const [customersRefreshKey, setCustomersRefreshKey] = useState(0);
  const [customerCountOverride, setCustomerCountOverride] = useState<number>();
  // Tabs unmount on navigation, so retain the last default-view total while Calls reloads.
  const [lastUnfilteredCallsCount, setLastUnfilteredCallsCount] = useState<number>();
  const [isAgentCreateSyncing, setIsAgentCreateSyncing] = useState(false);
  const [conversationSaveError, setConversationSaveError] = useState<string | null>(null);
  const [hasAttemptedConversationSave, setHasAttemptedConversationSave] = useState(false);
  const [pendingCampaignTab, setPendingCampaignTab] = useState<SidebarTabNamesType | null>(null);
  const [isRouterNavigationBlocked, setIsRouterNavigationBlocked] = useState(false);
  const [savedCampaignTitle, setSavedCampaignTitle] = useState<string | null>(null);
  const [isCampaignSectionSaving, setIsCampaignSectionSaving] = useState(false);
  const [isProductDetailsSectionSaving, setIsProductDetailsSectionSaving] = useState(false);
  const [productDetailsRevision, setProductDetailsRevision] = useState(0);
  const [isCallAnalysisDirty, setIsCallAnalysisDirty] = useState(false);
  const [isCallAnalysisBusy, setIsCallAnalysisBusy] = useState(false);
  const [isSquadsDirty, setIsSquadsDirty] = useState(false);
  const handleCallsCountChange = useCallback((count: number) => {
    setLastUnfilteredCallsCount((previous) => (previous === count ? previous : count));
  }, []);
  const handleCustomersCountChange = useCallback((count: number) => {
    setCustomerCountOverride((previous) => (previous === count ? previous : count));
  }, []);
  const [isSipTrunkDirty, setIsSipTrunkDirty] = useState(false);
  const [isSipTrunkBusy, setIsSipTrunkBusy] = useState(false);
  const savedGeneralValuesRef = useRef<CampaignGeneralFormValues | null>(null);
  const pendingSavedGeneralValuesRef = useRef<CampaignGeneralFormValues | null>(null);
  const savedCustomersRef = useRef<CreateCampaignFormValue['customers'] | null>(null);
  const savedProductDetailsRef = useRef<{ id: string; productDetails: string | null } | null>(null);
  const pendingSavedProductDetailsRef = useRef<{
    id: string;
    productDetails: string | null;
  } | null>(null);
  const routerNavigationResolveRef = useRef<((proceed: boolean) => void) | null>(null);
  const campaignSaveInFlightRef = useRef(false);
  const productDetailsSaveInFlightRef = useRef(false);
  const leadListChangedRef = useRef(leadListChanged);
  const callAnalysisActionsRef = useRef<CallAnalysisActions | null>(null);
  const sipTrunkActionsRef = useRef<SipTrunkActions | null>(null);
  const squadsActionsRef = useRef<{ save: () => Promise<boolean>; discard: () => void } | null>(
    null,
  );
  const titleCampaignIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    leadListChangedRef.current = leadListChanged;
  }, [leadListChanged]);

  const hasValidCampaignId = useMemo(
    () => Boolean(campaignId && UUID_ROUTE_PARAM_PATTERN.test(campaignId)),
    [campaignId],
  );

  const {
    data: campaignData,
    refetch,
    isLoading,
    error: campaignFetchError,
  } = useCampaignControllerCampaignQuery(
    { id: campaignId! },
    {
      skip: !hasValidCampaignId,
      refetchOnMountOrArgChange: true,
    },
  );

  const isNotFound = useMemo(
    () =>
      Boolean(
        !hasValidCampaignId ||
        (!campaignData &&
          campaignFetchError &&
          'status' in campaignFetchError &&
          campaignFetchError.status === 404),
      ),
    [campaignData, campaignFetchError, hasValidCampaignId],
  );

  const isSquadCampaign = !!campaignData?.squad;

  const activeTab = useEditCampaignTabRouting({ campaignData, isSquadCampaign });

  useCampaignRealtimeSync({
    campaignId,
    companyId: campaignData?.companyId,
  });

  useEditCampaignLayout(campaignData);

  const {
    data: campaignAgents,
    refetch: refetchCampaignAgents,
    isLoading: isLoadingCampaignAgents,
  } = useCampaignAgents(campaignId, isSquadCampaign || !campaignData || isNotFound);

  const agents = useMemo(() => {
    if (isSquadCampaign) {
      return campaignData?.squad?.agents || [];
    }
    return campaignAgents ?? [];
  }, [isSquadCampaign, campaignData, campaignAgents]);

  const hasAgents = useMemo(() => {
    if (isSquadCampaign) {
      return (campaignData?.squad?.agents?.length ?? 0) > 0;
    }
    if (agents.length > 0) {
      return true;
    }
    if (isAgentCreateSyncing || isLoadingCampaignAgents) {
      return (campaignData?.agentsCount ?? 0) > 0;
    }
    return false;
  }, [
    agents.length,
    campaignData?.agentsCount,
    isAgentCreateSyncing,
    isLoadingCampaignAgents,
    isSquadCampaign,
  ]);

  const handleAgentCreated = useCallback(async () => {
    setIsAgentCreateSyncing(true);

    try {
      await refetch();

      if (!isSquadCampaign) {
        await refetchCampaignAgents();
      }
    } finally {
      setIsAgentCreateSyncing(false);
    }
  }, [isSquadCampaign, refetch, refetchCampaignAgents]);

  const isAmbienceSettingsTab = activeTab === SidebarTabNames.AMBIENCE_SETTINGS;
  const isNoiseProcessingTab = activeTab === SidebarTabNames.NOISE_PROCESSING;
  const isConversationSettingsTab = activeTab === SidebarTabNames.CONVERSATION_SETTINGS;
  const isConversationSettingsReadOnly =
    currentUser?.role === UserRolesEnum.VIEWER ||
    campaignData?.status !== CampaignRunStatusType.INACTIVE;
  const {
    sounds: backgroundSounds,
    isLoadingSounds: isLoadingBackgroundSounds,
    draft: ambienceSettingsDraft,
    setDraft: setAmbienceSettingsDraft,
    isDirty: isAmbienceSettingsDirty,
    hasValidationError: hasAmbienceSettingsValidationError,
    hasMissingSounds: hasAmbienceSettingsMissingSounds,
    isReadOnly: isAmbienceSettingsReadOnly,
    disableReason: ambienceSettingsDisableReason,
    hasAttemptedSave: hasAttemptedAmbienceSettingsSave,
    isSaving: isSavingAmbienceSettings,
    save: handleAmbienceSettingsSave,
    cancel: handleAmbienceSettingsCancel,
  } = useCampaignAmbienceSettings({
    campaign: campaignData,
    currentUserRole: currentUser?.role,
    refetch,
  });

  const {
    isDirty: isNoiseProcessingDirty,
    isReadOnly: isNoiseProcessingReadOnly,
    visibleValidationErrors: visibleNoiseProcessingErrors,
    saveError: noiseSaveError,
    isSaving: isSavingNoiseProcessing,
    save: handleNoiseProcessingSave,
    cancel: handleNoiseProcessingCancel,
    draft: noiseProcessingDraft,
    setDraft: setNoiseProcessingDraft,
  } = useCampaignNoiseProcessingSettings({
    campaign: campaignData,
    currentUserRole: currentUser?.role,
    refetch,
  });
  const {
    draft: conversationSettingsDraft,
    setDraft: setConversationSettingsDraft,
    resetDraft: resetConversationSettingsDraft,
    isDirty: isConversationSettingsDirty,
    validationErrors: conversationSettingsErrors,
  } = useConversationSettingsController(campaignData);
  const hasConversationSettingsValidationErrors = Object.values(conversationSettingsErrors).some(
    (errors) => Object.keys(errors).length > 0,
  );
  const visibleConversationSettingsErrors: ConversationSettingsValidation =
    hasAttemptedConversationSave
      ? conversationSettingsErrors
      : { turnHandling: {}, speechPacing: {} };

  const {
    currentData: campaignCompany,
    isSuccess: isCompanyFetched,
    isError: isCampaignCompanyError,
  } = useCompanyControllerGetCompanyQuery(
    { id: (campaignData as unknown as CampaignDto)?.companyId },
    { skip: !(campaignData as unknown as CampaignDto)?.companyId },
  );

  // Agents whose voice, transcriber or LLM is no longer available block the campaign start
  const agentsWithRemovedSettings = useMemo<AgentRemovedSettingsBlockerInput[]>(
    () =>
      agents
        .map((agent) => ({
          agentName: agent.name,
          settingKeys: getRemovedAgentSettingNameKeys(
            getRemovedAgentSettings(agent, campaignCompany),
          ),
        }))
        .filter(({ settingKeys }) => settingKeys.length > 0),
    [agents, campaignCompany],
  );
  const agentSettingsCheck: AgentSettingsCheckStatus = campaignCompany
    ? 'ready'
    : isCampaignCompanyError
      ? 'failed'
      : 'pending';

  const { data: campaignSipTrunk } = useCampaignSipTrunkControllerGetSipTrunkQuery(
    { id: campaignId! },
    { skip: !hasValidCampaignId || isNotFound },
  );

  const { data: customersFirstPage, refetch: refetchCustomers } =
    useCustomerListPaginatedControllerGetCustomerListPaginatedQuery(
      { campaignId: campaignId!, limit: 20 },
      { skip: !hasValidCampaignId || isNotFound, refetchOnMountOrArgChange: true },
    );

  const hasRealCustomers = useMemo(
    () =>
      (customersFirstPage?.data ?? []).some((customer) => Boolean(customer.number)) ||
      Boolean(customersFirstPage?.meta?.hasMore),
    [customersFirstPage],
  );

  const handleCampaignSaveError = useCallback(
    (error: unknown) => {
      const data =
        error && typeof error === 'object' && 'data' in error
          ? (error as { data?: unknown }).data
          : undefined;

      if (data && typeof data === 'object' && 'customers' in data) {
        const message =
          extractFirstErrorMessage((data as { customers: unknown }).customers) ??
          t('adminMainPage.campaigns.somethingWentWrong');

        toast.error((props) => <CustomErrorToast {...props} message={message} />, {
          closeButton: false,
          autoClose: 5000,
          icon: false,
        });
        return;
      }

      toast.error(
        (props) => (
          <CustomErrorToast {...props} message={t('adminMainPage.campaigns.somethingWentWrong')} />
        ),
        {
          closeButton: false,
          autoClose: 5000,
          icon: false,
        },
      );
    },
    [t],
  );

  const { saveCampaignForm, formMethods } = useEditCampaign({
    CustomersListChanged: leadListChangedRef,
    onSaveError: handleCampaignSaveError,
  });

  const { control, reset, resetField, setError, clearErrors } = formMethods;
  const { errors: formStateErrors, dirtyFields } = useFormState({ control });
  const isGeneralSectionDirty = GENERAL_CAMPAIGN_FIELDS.some((fieldName) =>
    Boolean(dirtyFields[fieldName]),
  );
  const isProductDetailsDirty = Boolean(dirtyFields.productDetails);
  const isCustomerSectionDirty =
    leadListChanged || Boolean(dirtyFields.customers) || Boolean(dirtyFields.customerListChanged);
  const isCampaignPageDirty =
    isGeneralSectionDirty ||
    isCustomerSectionDirty ||
    isProductDetailsDirty ||
    isAmbienceSettingsDirty ||
    isConversationSettingsDirty ||
    isCallAnalysisDirty ||
    isSquadsDirty ||
    isNoiseProcessingDirty ||
    isSipTrunkDirty;
  const dirtyStateByTab: Partial<Record<SidebarTabNamesType, boolean>> = {
    [SidebarTabNames.GENERAL]: isGeneralSectionDirty,
    [SidebarTabNames.PHONE_NUMBERS]: isCustomerSectionDirty,
    [SidebarTabNames.AGENT]: isProductDetailsDirty,
    [SidebarTabNames.NOISE_PROCESSING]: isNoiseProcessingDirty,
    [SidebarTabNames.AMBIENCE_SETTINGS]: isAmbienceSettingsDirty,
    [SidebarTabNames.CONVERSATION_SETTINGS]: isConversationSettingsDirty,
    [SidebarTabNames.CALL_ANALYSIS]: isCallAnalysisDirty,
    [SidebarTabNames.SQUADS]: isSquadsDirty,
    [SidebarTabNames.SIP_TRUNK]: isSipTrunkDirty,
  };
  const isActiveCampaignSectionDirty = activeTab !== null && dirtyStateByTab[activeTab] === true;

  const handleCallAnalysisDirtyChange = useCallback((dirty: boolean) => {
    setIsCallAnalysisDirty(dirty);
  }, []);

  const handleCallAnalysisActionsChange = useCallback((actions: CallAnalysisActions | null) => {
    callAnalysisActionsRef.current = actions;
    setIsCallAnalysisBusy(actions?.isBusy ?? false);
  }, []);

  const handleSipTrunkDirtyChange = useCallback((dirty: boolean) => {
    setIsSipTrunkDirty(dirty);
  }, []);

  const handleSipTrunkActionsChange = useCallback((actions: SipTrunkActions | null) => {
    sipTrunkActionsRef.current = actions;
    setIsSipTrunkBusy(actions?.isBusy ?? false);
  }, []);

  const handleSquadsDirtyChange = useCallback((dirty: boolean) => {
    setIsSquadsDirty(dirty);
  }, []);

  const handleSquadsActionsChange = useCallback(
    (actions: { save: () => Promise<boolean>; discard: () => void } | null) => {
      squadsActionsRef.current = actions;
    },
    [],
  );

  useEffect(() => {
    if (!campaignData) return;

    const campaignFormValues = mapCampaignDtoToFormValues(campaignData);
    const isCampaignIdentityChanged = titleCampaignIdRef.current !== campaignData.id;

    if (isCampaignIdentityChanged) {
      const previousCampaignId = titleCampaignIdRef.current;
      const campaignTitle = campaignData.title?.trim() || null;
      titleCampaignIdRef.current = campaignData.id;
      savedGeneralValuesRef.current = getCampaignGeneralFormValues(campaignFormValues);
      savedProductDetailsRef.current = {
        id: campaignData.id,
        productDetails: campaignFormValues.productDetails ?? null,
      };
      savedCustomersRef.current = null;
      pendingSavedGeneralValuesRef.current = null;
      pendingSavedProductDetailsRef.current = null;
      setSavedCampaignTitle(campaignTitle);
      reset(campaignFormValues);
      setLeadListChanged(false);
      setCustomerCountOverride(undefined);
      if (previousCampaignId !== undefined) {
        setCustomersRefreshKey((key) => key + 1);
      }
      return;
    }

    if (!savedGeneralValuesRef.current) {
      savedGeneralValuesRef.current = getCampaignGeneralFormValues(campaignFormValues);
    }
    if (!savedProductDetailsRef.current) {
      savedProductDetailsRef.current = {
        id: campaignData.id,
        productDetails: campaignFormValues.productDetails ?? null,
      };
    }

    const pendingSavedGeneralValues = pendingSavedGeneralValuesRef.current;
    const pendingSavedProductDetails = pendingSavedProductDetailsRef.current;
    const isStaleCampaignDataAfterSave =
      pendingSavedGeneralValues?.id === campaignData.id &&
      !GENERAL_CAMPAIGN_FIELDS.every((fieldName) =>
        isEqual(campaignFormValues[fieldName], pendingSavedGeneralValues[fieldName]),
      );
    const isStaleProductDetailsAfterSave =
      pendingSavedProductDetails?.id === campaignData.id &&
      !isEqual(campaignFormValues.productDetails, pendingSavedProductDetails.productDetails);
    const isDifferentFromSavedGeneralBaseline =
      savedGeneralValuesRef.current?.id === campaignData.id &&
      !GENERAL_CAMPAIGN_FIELDS.every((fieldName) =>
        isEqual(campaignFormValues[fieldName], savedGeneralValuesRef.current?.[fieldName]),
      );
    const isDifferentFromSavedProductDetailsBaseline =
      savedProductDetailsRef.current?.id === campaignData.id &&
      !isEqual(campaignFormValues.productDetails, savedProductDetailsRef.current.productDetails);

    if (
      isStaleCampaignDataAfterSave ||
      isStaleProductDetailsAfterSave ||
      (pendingSavedGeneralValues === null && isDifferentFromSavedGeneralBaseline) ||
      (pendingSavedProductDetails === null && isDifferentFromSavedProductDetailsBaseline)
    ) {
      return;
    }

    pendingSavedGeneralValuesRef.current = null;
    pendingSavedProductDetailsRef.current = null;
    if (isCampaignPageDirty || leadListChangedRef.current) return;

    savedGeneralValuesRef.current = getCampaignGeneralFormValues(campaignFormValues);
    savedProductDetailsRef.current = {
      id: campaignData.id,
      productDetails: campaignFormValues.productDetails ?? null,
    };
    reset(campaignFormValues);
  }, [campaignData, isCampaignPageDirty, reset]);

  const requestRouterNavigationConfirmation = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      routerNavigationResolveRef.current = resolve;
      setIsRouterNavigationBlocked(true);
    });
  }, []);

  useBeforeUnload(() => isCampaignPageDirty);
  useBlocker(isCampaignPageDirty, requestRouterNavigationConfirmation);

  const resetGeneralCampaignFields = useCallback(
    (values: Partial<CreateCampaignFormValue>) => {
      resetField('title', { defaultValue: values.title ?? '' });
      resetField('reservedLimit', { defaultValue: values.reservedLimit });
      resetField('retryIntervalInMinutes', { defaultValue: values.retryIntervalInMinutes ?? 0 });
      resetField('retryMaxNumber', { defaultValue: values.retryMaxNumber ?? 0 });
      resetField('retriesPerDay', { defaultValue: values.retriesPerDay ?? 0 });
      resetField('callSchedule', { defaultValue: values.callSchedule });
    },
    [resetField],
  );

  const {
    isRunningLoading,
    isStoppingLoading,
    showScheduleWarningDialogFlag,
    setShowScheduleWarningDialogFlag,
    showUsageLimitWarningDialog,
    setShowUsageLimitWarningDialog,
    showUsageLimitBlockedDialog,
    setShowUsageLimitBlockedDialog,
    showUsageLimitVerifyFailedDialog,
    setShowUsageLimitVerifyFailedDialog,
    timeUntilStartCampaignRun,
    closeScheduleDialog,
    runCampaign,
    handleUsageLimitWarningConfirm,
  } = useCampaignStartStop({ campaignData, refetch });

  const { issuedTabs, tabIssueMessages, forceValidate } = useCampaignStartValidation({
    campaignData,
    hasRealCustomers,
    campaignSipTrunk,
    formStateErrors,
    hasAgents,
    isLoading,
    isLoadingCampaignAgents,
    isRunningLoading,
    isStoppingLoading,
    currentUserRole: currentUser?.role,
    agentsWithRemovedSettings,
    agentSettingsCheck,
    setError,
    clearErrors,
  });

  const refetchCampaignAndCustomers = useCallback(async () => {
    await Promise.all([refetch(), refetchCustomers()]);
  }, [refetch, refetchCustomers]);

  const handleCampaignSave = useCallback(
    async (includeCustomers: boolean) => {
      if (campaignSaveInFlightRef.current || isCampaignSectionSaving) return false;

      campaignSaveInFlightRef.current = true;
      setIsCampaignSectionSaving(true);

      try {
        const isValid = await formMethods.trigger(
          includeCustomers ? 'customers' : [...GENERAL_CAMPAIGN_FIELDS],
        );
        forceValidate();

        if (!isValid) return false;

        const currentValues = formMethods.getValues();
        const valuesToSave = savedProductDetailsRef.current
          ? {
              ...currentValues,
              productDetails: savedProductDetailsRef.current.productDetails,
            }
          : currentValues;
        const isSaved = await saveCampaignForm(valuesToSave, { includeCustomers });
        if (!isSaved) return false;

        if (includeCustomers) {
          savedCustomersRef.current = valuesToSave.customers ?? [];
        } else {
          const savedGeneralValues = getCampaignGeneralFormValues(valuesToSave);
          savedGeneralValuesRef.current = savedGeneralValues;
          pendingSavedGeneralValuesRef.current = savedGeneralValues;
          const savedTitle = valuesToSave.title?.trim() || null;
          setSavedCampaignTitle(savedTitle);
        }

        try {
          await refetchCampaignAndCustomers();
        } catch (error) {
          handleCampaignSaveError(error);
          return false;
        }

        if (includeCustomers) {
          resetField('customers', { defaultValue: savedCustomersRef.current ?? [] });
          resetField('customerListChanged', { defaultValue: false });
          setLeadListChanged(false);
          setCustomerCountOverride(undefined);
          setCustomersRefreshKey((key) => key + 1);
        } else {
          resetGeneralCampaignFields(valuesToSave);
        }

        toast.success(CustomSuccessToast, {
          closeButton: false,
          autoClose: 5000,
          icon: false,
          data: {
            title: t('campaignPage.savedCampaignToastTitle'),
            description: t('campaignPage.savedCampaignToastBody'),
          },
        });

        return true;
      } catch (error) {
        handleCampaignSaveError(error);
        return false;
      } finally {
        campaignSaveInFlightRef.current = false;
        setIsCampaignSectionSaving(false);
      }
    },
    [
      forceValidate,
      formMethods,
      handleCampaignSaveError,
      isCampaignSectionSaving,
      refetchCampaignAndCustomers,
      resetField,
      resetGeneralCampaignFields,
      saveCampaignForm,
      setLeadListChanged,
      t,
    ],
  );

  const handleCampaignCancel = useCallback(() => {
    if (!campaignData) return;

    if (activeTab === SidebarTabNames.PHONE_NUMBERS) {
      resetField('customers', { defaultValue: savedCustomersRef.current ?? undefined });
      resetField('customerListChanged', { defaultValue: false });
      setLeadListChanged(false);
      setCustomerCountOverride(undefined);
      setCustomersRefreshKey((key) => key + 1);
      return;
    }

    resetGeneralCampaignFields(
      savedGeneralValuesRef.current ??
        getCampaignGeneralFormValues(mapCampaignDtoToFormValues(campaignData)),
    );
  }, [activeTab, campaignData, resetField, resetGeneralCampaignFields, setLeadListChanged]);

  const handleProductDetailsSave = useCallback(async () => {
    if (
      !campaignData?.id ||
      !isProductDetailsDirty ||
      currentUser?.role === UserRolesEnum.VIEWER ||
      productDetailsSaveInFlightRef.current ||
      isProductDetailsSectionSaving
    ) {
      return false;
    }

    productDetailsSaveInFlightRef.current = true;
    setIsProductDetailsSectionSaving(true);
    const productDetails = formMethods.getValues('productDetails') ?? null;

    try {
      await updateCampaignProductDetails({
        id: campaignData.id,
        campaignUpdateRequestDto: { productDetails },
      }).unwrap();

      const savedProductDetails = {
        id: campaignData.id,
        productDetails,
      };
      savedProductDetailsRef.current = savedProductDetails;
      pendingSavedProductDetailsRef.current = savedProductDetails;

      await refetch();

      resetField('productDetails', { defaultValue: productDetails });

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('campaignPage.savedCampaignToastTitle'),
          description: t('campaignPage.savedCampaignToastBody'),
        },
      });

      return true;
    } catch (error) {
      handleCampaignSaveError(error);
      return false;
    } finally {
      productDetailsSaveInFlightRef.current = false;
      setIsProductDetailsSectionSaving(false);
    }
  }, [
    campaignData?.id,
    currentUser?.role,
    formMethods,
    handleCampaignSaveError,
    isProductDetailsDirty,
    isProductDetailsSectionSaving,
    refetch,
    resetField,
    t,
    updateCampaignProductDetails,
  ]);

  const handleProductDetailsCancel = useCallback(() => {
    if (!campaignData) return;

    const savedProductDetails = savedProductDetailsRef.current;
    const resetProductDetails =
      savedProductDetails?.id === campaignData.id
        ? savedProductDetails.productDetails
        : mapCampaignDtoToFormValues(campaignData).productDetails;

    const restoredProductDetails = resetProductDetails ?? null;
    formMethods.setValue('productDetails', restoredProductDetails, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    resetField('productDetails', { defaultValue: restoredProductDetails });
    setProductDetailsRevision((revision) => revision + 1);
  }, [campaignData, formMethods, resetField]);

  // Conversation settings remain page-owned because their controller has not
  // been included in the extracted ambience/noise save-flow hooks.
  const handleConversationSettingsSave = useCallback(async (): Promise<boolean> => {
    if (!campaignData?.id || !conversationSettingsDraft || isConversationSettingsReadOnly) {
      return false;
    }
    setConversationSaveError(null);
    if (hasConversationSettingsValidationErrors) {
      setHasAttemptedConversationSave(true);
      return false;
    }

    try {
      await updateConversationSettings({
        id: campaignData.id,
        campaignUpdateRequestDto: mapConversationSettingsDraftToRequest(conversationSettingsDraft),
      }).unwrap();
      setHasAttemptedConversationSave(false);
      toast.success(t('conversationSettings.saveSuccess'));
      await refetch();
      return true;
    } catch (error) {
      const message = extractFirstErrorMessage(error) ?? t('conversationSettings.saveError');
      setConversationSaveError(message);
      return false;
    }
  }, [
    campaignData?.id,
    conversationSettingsDraft,
    hasConversationSettingsValidationErrors,
    isConversationSettingsReadOnly,
    refetch,
    t,
    updateConversationSettings,
  ]);

  const handleConversationSettingsCancel = useCallback(() => {
    setConversationSaveError(null);
    setHasAttemptedConversationSave(false);
    resetConversationSettingsDraft();
  }, [resetConversationSettingsDraft]);

  const requestCampaignTabChange = useCallback(
    (tab: SidebarTabNamesType) => {
      const isBlocked = isActiveCampaignSectionDirty && tab !== activeTab;

      if (isBlocked) {
        setPendingCampaignTab(tab);
        return false;
      }

      return true;
    },
    [activeTab, campaignId, isActiveCampaignSectionDirty, isCampaignPageDirty, isSipTrunkDirty],
  );

  const handleRequestAgentsTab = useCallback(() => {
    if (requestCampaignTabChange(SidebarTabNames.AGENT)) {
      navigate(`${location.pathname}?tab=${encodeURIComponent(SidebarTabNames.AGENT)}`);
    }
  }, [location.pathname, navigate, requestCampaignTabChange]);

  const resolveRouterNavigation = useCallback((proceed: boolean) => {
    setIsRouterNavigationBlocked(false);
    routerNavigationResolveRef.current?.(proceed);
    routerNavigationResolveRef.current = null;
  }, []);

  const confirmCampaignTabDiscard = useCallback(() => {
    switch (activeTab) {
      case SidebarTabNames.AGENT:
        handleProductDetailsCancel();
        break;
      case SidebarTabNames.AMBIENCE_SETTINGS:
        handleAmbienceSettingsCancel();
        break;
      case SidebarTabNames.CONVERSATION_SETTINGS:
        handleConversationSettingsCancel();
        break;
      case SidebarTabNames.NOISE_PROCESSING:
        handleNoiseProcessingCancel();
        break;
      case SidebarTabNames.CALL_ANALYSIS:
        callAnalysisActionsRef.current?.discard();
        setIsCallAnalysisDirty(false);
        break;
      case SidebarTabNames.SIP_TRUNK:
        sipTrunkActionsRef.current?.discard();
        setIsSipTrunkDirty(false);
        break;
      case SidebarTabNames.SQUADS:
        squadsActionsRef.current?.discard();
        setIsSquadsDirty(false);
        break;
      default:
        handleCampaignCancel();
    }
    if (pendingCampaignTab) {
      const nextTab = pendingCampaignTab;
      setPendingCampaignTab(null);
      navigate(`${location.pathname}?tab=${encodeURIComponent(nextTab)}`);
      return;
    }

    resolveRouterNavigation(true);
  }, [
    activeTab,
    handleAmbienceSettingsCancel,
    handleCampaignCancel,
    handleConversationSettingsCancel,
    handleNoiseProcessingCancel,
    handleProductDetailsCancel,
    location.pathname,
    navigate,
    pendingCampaignTab,
    resolveRouterNavigation,
  ]);

  const confirmCampaignTabSave = useCallback(async () => {
    let isSaved = false;

    switch (activeTab) {
      case SidebarTabNames.AGENT:
        isSaved = await handleProductDetailsSave();
        break;
      case SidebarTabNames.AMBIENCE_SETTINGS:
        isSaved = await handleAmbienceSettingsSave();
        break;
      case SidebarTabNames.CONVERSATION_SETTINGS:
        isSaved = await handleConversationSettingsSave();
        break;
      case SidebarTabNames.NOISE_PROCESSING:
        isSaved = await handleNoiseProcessingSave();
        break;
      case SidebarTabNames.CALL_ANALYSIS:
        isSaved = (await callAnalysisActionsRef.current?.save()) ?? false;
        if (isSaved) {
          setIsCallAnalysisDirty(false);
        }
        break;
      case SidebarTabNames.SIP_TRUNK:
        isSaved = (await sipTrunkActionsRef.current?.save()) ?? false;
        if (isSaved) {
          setIsSipTrunkDirty(false);
        }
        break;
      case SidebarTabNames.SQUADS:
        isSaved = (await squadsActionsRef.current?.save()) ?? false;
        if (isSaved) {
          setIsSquadsDirty(false);
        } else {
          // Unblock the leave-tab dialog so the missing-rules warning can be
          // confirmed on Squads instead of silently auto-acking it.
          setPendingCampaignTab(null);
        }
        break;
      default:
        isSaved = await handleCampaignSave(activeTab === SidebarTabNames.PHONE_NUMBERS);
    }

    if (!isSaved) return;

    if (pendingCampaignTab) {
      const nextTab = pendingCampaignTab;
      setPendingCampaignTab(null);
      navigate(`${location.pathname}?tab=${encodeURIComponent(nextTab)}`);
      return;
    }

    resolveRouterNavigation(true);
  }, [
    activeTab,
    handleAmbienceSettingsSave,
    handleCampaignSave,
    handleConversationSettingsSave,
    handleNoiseProcessingSave,
    handleProductDetailsSave,
    location.pathname,
    navigate,
    pendingCampaignTab,
    resolveRouterNavigation,
  ]);

  const isCampaignNavigationSaving =
    isCampaignSectionSaving ||
    isProductDetailsSectionSaving ||
    isSavingAmbienceSettings ||
    isSavingConversationSettings ||
    isSavingNoiseProcessing ||
    isCallAnalysisBusy ||
    isSipTrunkBusy;

  const closeCampaignNavigationDialog = useCallback(() => {
    if (isRouterNavigationBlocked) {
      resolveRouterNavigation(false);
      return;
    }

    setPendingCampaignTab(null);
  }, [isRouterNavigationBlocked, resolveRouterNavigation]);

  if (activeTab === null || isLoading) {
    return <Spinner />;
  }

  if (isNotFound) {
    return (
      <PageContainer title={t('campaignPage.notFound.title')} companyName={currentCompany?.title}>
        <Box
          sx={{
            minHeight: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            py: 6,
          }}>
          <Box sx={{ maxWidth: 480, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              {t('campaignPage.notFound.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {t('campaignPage.notFound.description')}
            </Typography>
            <Button
              component={Link}
              to={AuthenticatedPath.CAMPAIGN}
              variant="contained"
              color="primary">
              {t('campaignPage.notFound.backToList')}
            </Button>
          </Box>
        </Box>
      </PageContainer>
    );
  }

  const pageTitle =
    titleCampaignIdRef.current === campaignData?.id
      ? savedCampaignTitle || t('campaignPage.editCampaign.editCampaign')
      : campaignData?.title?.trim() || t('campaignPage.editCampaign.editCampaign');

  return (
    <PageContainer title={pageTitle} companyName={currentCompany?.title}>
      <Box sx={{ display: 'flex', flexDirection: 'row', minHeight: '100%', height: 'max-content' }}>
        <EditCampaignSidebar
          activeTab={activeTab}
          issuedTabs={issuedTabs}
          tabIssueMessages={tabIssueMessages}
          campaignType={campaignData?.type}
          hasSquad={isSquadCampaign}
          onTabChange={requestCampaignTabChange}
        />
        <Grid
          container
          sx={{
            overflow: 'hidden',
            flexDirection: 'column',
            flexWrap: 'nowrap',
            flex: 1,
            minWidth: 0,
          }}>
          {isAmbienceSettingsTab && campaignData ? (
            <EditCampaignSectionActions
              mode="ambience"
              isReadOnly={isAmbienceSettingsReadOnly}
              isDirty={isAmbienceSettingsDirty}
              isSaving={isSavingAmbienceSettings}
              isLoadingSounds={isLoadingBackgroundSounds}
              hasValidationError={hasAmbienceSettingsValidationError}
              hasMissingSounds={hasAmbienceSettingsMissingSounds}
              disableReason={ambienceSettingsDisableReason}
              onSave={() => void handleAmbienceSettingsSave()}
              onCancel={handleAmbienceSettingsCancel}
            />
          ) : null}
          {isNoiseProcessingTab && campaignData ? (
            <EditCampaignSectionActions
              mode="noise"
              isReadOnly={isNoiseProcessingReadOnly}
              isDirty={isNoiseProcessingDirty}
              isSaving={isSavingNoiseProcessing}
              hasValidationErrors={Object.values(visibleNoiseProcessingErrors).some(
                (errors) => Object.keys(errors).length > 0,
              )}
              saveError={noiseSaveError}
              onSave={() => void handleNoiseProcessingSave()}
              onCancel={handleNoiseProcessingCancel}
            />
          ) : null}
          {isConversationSettingsTab && campaignData ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 3,
                px: 4,
                pt: 3,
              }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" fontWeight={600}>
                  {t('conversationSettings.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('conversationSettings.description')}
                </Typography>
                {isConversationSettingsReadOnly ? (
                  <Typography variant="body2" color="warning.main">
                    {t('conversationSettings.disabledCampaign')}
                  </Typography>
                ) : null}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <CampaignSectionSaveActions
                  isReadOnly={isConversationSettingsReadOnly}
                  isDirty={isConversationSettingsDirty}
                  isSaving={isSavingConversationSettings}
                  onSave={() => void handleConversationSettingsSave()}
                  onCancel={handleConversationSettingsCancel}
                />
                {conversationSaveError ? (
                  <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                    {conversationSaveError}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          ) : null}
          <FormProvider {...formMethods}>
            <AnimatedContent contentKey={activeTab} presenceMode="wait" instantExit>
              <EditCampaignTabContent
                activeTab={activeTab}
                campaignData={campaignData}
                campaignId={campaignId}
                companyName={formatCompanyDisplayName(campaignCompany?.name)}
                isLoading={isLoading}
                isLoadingCampaignAgents={isLoadingCampaignAgents}
                currentUserRole={currentUser?.role}
                isSquadCampaign={isSquadCampaign}
                isCompanyFetched={isCompanyFetched}
                agents={agents}
                onAgentCreated={handleAgentCreated}
                isProductDetailsDirty={isProductDetailsDirty}
                isSavingProductDetails={isProductDetailsSectionSaving}
                onProductDetailsSave={handleProductDetailsSave}
                onProductDetailsCancel={handleProductDetailsCancel}
                productDetailsRevision={productDetailsRevision}
                refetch={refetch}
                refetchCampaignAgents={refetchCampaignAgents}
                responseLoadingFlag={isCampaignSectionSaving}
                setLeadListChanged={setLeadListChanged}
                customersRefreshKey={customersRefreshKey}
                initialCustomersCount={customerCountOverride}
                onCustomersCountChange={handleCustomersCountChange}
                initialCallsCount={lastUnfilteredCallsCount}
                onCallsCountChange={handleCallsCountChange}
                isLoadingBackgroundSounds={isLoadingBackgroundSounds}
                backgroundSounds={backgroundSounds}
                ambienceSettingsDraft={ambienceSettingsDraft}
                setAmbienceSettingsDraft={setAmbienceSettingsDraft}
                isAmbienceSettingsReadOnly={isAmbienceSettingsReadOnly}
                hasAttemptedAmbienceSettingsSave={hasAttemptedAmbienceSettingsSave}
                generalSectionActions={
                  <CampaignSectionSaveActions
                    isReadOnly={currentUser?.role === UserRolesEnum.VIEWER}
                    isDirty={isActiveCampaignSectionDirty}
                    isSaving={isCampaignSectionSaving}
                    onSave={() => void handleCampaignSave(false)}
                    onCancel={handleCampaignCancel}
                  />
                }
                phoneNumbersSectionActions={
                  <CampaignSectionSaveActions
                    isReadOnly={currentUser?.role === UserRolesEnum.VIEWER}
                    isDirty={isActiveCampaignSectionDirty}
                    isSaving={isCampaignSectionSaving}
                    onSave={() => void handleCampaignSave(true)}
                    onCancel={handleCampaignCancel}
                  />
                }
                noiseProcessing={{
                  draft: noiseProcessingDraft,
                  setDraft: setNoiseProcessingDraft,
                  errors: visibleNoiseProcessingErrors,
                  isReadOnly: isNoiseProcessingReadOnly,
                }}
                conversationSettings={{
                  draft: conversationSettingsDraft,
                  setDraft: (draft: ConversationSettingsDraft) =>
                    setConversationSettingsDraft(draft),
                  errors: visibleConversationSettingsErrors,
                  isReadOnly: isConversationSettingsReadOnly,
                }}
                callAnalysis={{
                  onDirtyChange: handleCallAnalysisDirtyChange,
                  onActionsChange: handleCallAnalysisActionsChange,
                }}
                sipTrunk={{
                  onDirtyChange: handleSipTrunkDirtyChange,
                  onActionsChange: handleSipTrunkActionsChange,
                }}
                squads={{
                  onDirtyChange: handleSquadsDirtyChange,
                  onActionsChange: handleSquadsActionsChange,
                  onRequestAgentsTab: handleRequestAgentsTab,
                }}
              />
            </AnimatedContent>
          </FormProvider>
        </Grid>
      </Box>
      <EditCampaignDialogs
        showScheduleWarningDialogFlag={showScheduleWarningDialogFlag}
        onCloseScheduleDialog={closeScheduleDialog}
        setShowScheduleWarningDialogFlag={setShowScheduleWarningDialogFlag}
        timeUntilStartCampaignRun={timeUntilStartCampaignRun}
        onRunCampaign={runCampaign}
        showUsageLimitWarningDialog={showUsageLimitWarningDialog}
        setShowUsageLimitWarningDialog={setShowUsageLimitWarningDialog}
        onUsageLimitWarningConfirm={handleUsageLimitWarningConfirm}
        showUsageLimitBlockedDialog={showUsageLimitBlockedDialog}
        setShowUsageLimitBlockedDialog={setShowUsageLimitBlockedDialog}
        showUsageLimitVerifyFailedDialog={showUsageLimitVerifyFailedDialog}
        setShowUsageLimitVerifyFailedDialog={setShowUsageLimitVerifyFailedDialog}
        campaignNavigation={{
          open: Boolean(pendingCampaignTab) || isRouterNavigationBlocked,
          isSaving: isCampaignNavigationSaving,
          onClose: closeCampaignNavigationDialog,
          onDiscard: confirmCampaignTabDiscard,
          onSave: confirmCampaignTabSave,
        }}
      />
    </PageContainer>
  );
};
